import React, { useRef, useState, useCallback } from 'react';
import { styled } from '@mui/material';
import { MarkerClusterer, MarkerClustererProps } from '@react-google-maps/api';
import { Clusterer, MarkerExtended } from '@react-google-maps/marker-clusterer';

import { IStore } from '@ocx-app/modules/stores/types';

import { StoreMarker } from './store-marker.component';

const CLUSTER_GRID_SIZE = 30;

type StyledMarkerClustererProps = Omit<MarkerClustererProps, 'clusterClass'> & { className: string };

const StyledMarkerClusterer = styled(({ className, ...props }: StyledMarkerClustererProps) => (
  <MarkerClusterer clusterClass={className} {...props} />
))(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  '& img': {
    display: 'none',
  },

  '& div': {
    backgroundColor: theme.palette.primary.main,

    position: 'unset !important',
    top: 'unset !important',
    left: 'unset !important',
    lineHeight: 'unset !important',
    width: 'unset !important',

    fontSize: 11,
    textAlign: 'center',
    minWidth: 26,
    borderRadius: 20,
    padding: 6,
    color: `${theme.palette.primary.contrastText} !important`,
    fontWeight: '600 !important',
    border: 'solid 1px white',
  },
}));

type StoreMarkersProp = {
  stores: IStore[];
  onStorePinClick(store: IStore): void;
};

export const StoreMarkers: React.FC<StoreMarkersProp> = (props) => {
  const { stores, onStorePinClick } = props;

  const clustererRef = useRef<null | Clusterer>(null);
  const [visibleMarkers, setVisibleMarkers] = useState<MarkerExtended[]>([]);

  const onClusteringEnd = useCallback(() => {
    // Search non-clustered markers
    if (clustererRef.current !== null) {
      const markers: MarkerExtended[] = [];
      clustererRef.current?.getClusters().forEach((cluster) => {
        if (cluster.markers.length === 1) {
          markers.push(cluster.markers[0]);
        }
      });
      setVisibleMarkers(markers);
    }
  }, []);

  const render = useCallback(
    (clusterer: Clusterer) => {
      clustererRef.current = clusterer;
      return stores.map((store) => {
        return (
          <StoreMarker
            key={store.id}
            id={store.id}
            position={{
              lat: store.lat,
              lng: store.lng,
            }}
            price={store.price}
            isInCluster={!visibleMarkers.find((m) => m.getTitle() === store.id)}
            clusterer={clusterer}
            handleSelectStore={() => onStorePinClick(store)}
          />
        );
      });
    },
    [onStorePinClick, stores, visibleMarkers],
  );

  return (
    <StyledMarkerClusterer averageCenter gridSize={CLUSTER_GRID_SIZE} onClusteringEnd={onClusteringEnd}>
      {/* New types version required singe Element as child */}
      {/* @ts-expect-error */}
      {render}
    </StyledMarkerClusterer>
  );
};
